import {
    Box,
    Card,
    Checkbox,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableCellProps,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from '@mui/material';
import { ReactNode, useState } from 'react';
import { Scrollbar } from '@/components/Scrollbar/Scrollbar';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

export type StajackTableColumn<T> = {
    label: string;
    sortKey?: string;
    itemKey?: keyof T;
    itemTransform?: (item: T) => string | number | ReactNode;
    options?: {
        headerCellProps?: TableCellProps;
        rowCellProps?: TableCellProps;
    };
};

export type StarjackTableProps<T> = {
    count: number;
    items: T[];
    allItems?: T[];
    columns: StajackTableColumn<T>[];
    itemId?: keyof T;
    rowsPerPage?: number;
    onRowsPerPageChange?: (nextValue: number) => void;
    onSelectAll?: (allItems: T[]) => void;
    onDeselectAll?: () => void;
    onSelect?: (item: T, allSelected?: T[]) => void;
    onDeselect?: (item: T, nextSelected?: T[]) => void;
    onPageChange: (nextPage: number) => void;
    onRowClick?: (item: T) => void;
    rowsSelectable?: boolean;
    loading?: boolean;
    currentPage?: number;
    noDataLabel?: string;
    searchTerm?: string;
    defaultSortColumn?: string;
    defaultSortDirection?: 'asc' | 'desc';
    selectRow?: (item: T, rowIndex: number) => boolean;
};

// using function here because of an IDE linting error
export function StarjackTable<T>({
    items = [],
    allItems = items,
    columns,
    onDeselectAll,
    onSelectAll,
    itemId,
    onPageChange,
    onRowsPerPageChange,
    count = 0,
    rowsPerPage = 25,
    rowsSelectable = false,
    onSelect,
    onDeselect,
    loading = false,
    currentPage,
    onRowClick,
    noDataLabel = 'Keine Einträge vorhanden',
    searchTerm = '',
    defaultSortColumn = '',
    defaultSortDirection = 'desc',
    selectRow,
}: StarjackTableProps<T>) {
    const [page, setPage] = useState(currentPage || 0);
    const [selected, setSelected] = useState([]);

    const selectedSome = selected.length > 0 && selected.length < items.length;
    const selectedAll =
        allItems.length > 0 && selected.length === allItems.length;

    const onSelectItem = (checked: boolean, item: T) => {
        if (checked) {
            const nextSelected: T[] = [...selected, item];
            setSelected(() => nextSelected);
            onSelect?.(item, nextSelected);
        } else {
            const currentSelected = [...selected];

            const index = currentSelected.findIndex(
                (currentSelected) => currentSelected[itemId] === item[itemId]
            );

            if (index > -1) {
                currentSelected.splice(index, 1);

                setSelected(currentSelected);
                onDeselect?.(item, currentSelected);
            }
        }
    };

    const onItemSearch = (searchTerm: string, items: T[]): T[] => {
        return items.filter((item) => {
            return columns.some(({ itemKey, itemTransform }) => {
                const columnDisplayString = itemKey
                    ? item[itemKey]
                    : itemTransform(item);

                if (typeof columnDisplayString !== 'string') return false;

                const normalizedSearchTermSplit = searchTerm
                    .toLocaleLowerCase()
                    .split(' ');

                return normalizedSearchTermSplit.some((searchTermPart) =>
                    columnDisplayString
                        .toLocaleLowerCase()
                        .includes(searchTermPart)
                );
            });
        });
    };

    let itemsToRender: T[] = items;
    if (searchTerm.length > 0) {
        itemsToRender = onItemSearch(searchTerm, allItems);
    }

    return (
        <Card
            sx={{
                width: '100%',
            }}
        >
            <Scrollbar>
                <Box sx={{ minWidth: 800 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {rowsSelectable && (
                                    <TableCell padding='checkbox'>
                                        <Checkbox
                                            checked={selectedAll}
                                            indeterminate={selectedSome}
                                            onChange={(event) => {
                                                if (event.target.checked) {
                                                    setSelected(allItems);
                                                    onSelectAll?.(allItems);
                                                } else {
                                                    onDeselectAll?.();
                                                    setSelected([]);
                                                }
                                            }}
                                        />
                                    </TableCell>
                                )}

                                {columns.map(
                                    ({ label, options, sortKey }, index) => (
                                        <TableCell
                                            key={index}
                                            {...options?.headerCellProps}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                {label}

                                                {defaultSortColumn ===
                                                    sortKey && (
                                                    <>
                                                        {defaultSortDirection ===
                                                        'asc' ? (
                                                            <KeyboardArrowUpIcon />
                                                        ) : (
                                                            <KeyboardArrowDownIcon />
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                        </TableCell>
                                    )
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                new Array(rowsPerPage)
                                    .fill(null)
                                    .map((_value, index) => (
                                        <TableRow key={index}>
                                            <TableCell
                                                colSpan={
                                                    columns.length +
                                                    (rowsSelectable ? 1 : 0)
                                                }
                                            >
                                                <Skeleton height={40} />
                                            </TableCell>
                                        </TableRow>
                                    ))
                            ) : itemsToRender.length ? (
                                itemsToRender.map((item, index) => {
                                    const isSelected = !!selected.find(
                                        (selectedItem) =>
                                            selectedItem[itemId] ===
                                            item[itemId]
                                    );
                                    const showAsSelected =
                                        selectRow?.(item, index) || false;

                                    return (
                                        <TableRow
                                            hover
                                            key={index}
                                            selected={isSelected}
                                            onClick={() => {
                                                if (!rowsSelectable) {
                                                    onRowClick?.(item);
                                                }
                                            }}
                                            sx={{
                                                ...(!rowsSelectable && {
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        backgroundColor:
                                                            'rgba(0, 0, 0, 0.04)',
                                                    },
                                                }),
                                                ...(showAsSelected && {
                                                    backgroundColor:
                                                        'rgba(0, 0, 0, 0.04)',
                                                }),
                                            }}
                                        >
                                            {rowsSelectable && (
                                                <TableCell padding='checkbox'>
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onChange={(event) => {
                                                            onSelectItem(
                                                                event.target
                                                                    .checked,
                                                                item
                                                            );
                                                        }}
                                                    />
                                                </TableCell>
                                            )}

                                            {columns.map(
                                                (
                                                    {
                                                        itemKey,
                                                        itemTransform,
                                                        options,
                                                    },
                                                    index
                                                ) => (
                                                    <TableCell
                                                        {...options?.rowCellProps}
                                                        key={index}
                                                    >
                                                        <>
                                                            {!!itemKey &&
                                                                item[itemKey]}

                                                            {!itemKey &&
                                                            typeof itemTransform ===
                                                                'function'
                                                                ? itemTransform(
                                                                      item
                                                                  )
                                                                : ''}
                                                        </>
                                                    </TableCell>
                                                )
                                            )}
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell
                                        align='center'
                                        sx={{
                                            p: 6,
                                        }}
                                        colSpan={
                                            columns.length +
                                            (rowsSelectable ? 1 : 0)
                                        }
                                    >
                                        <Typography
                                            sx={{
                                                fontWeight: 500,
                                            }}
                                        >
                                            {noDataLabel}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </Scrollbar>
            <TablePagination
                component='div'
                count={count}
                page={currentPage === undefined ? page : currentPage}
                onPageChange={(_event, value) => {
                    setPage(value);
                    onPageChange(value);
                }}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                    onRowsPerPageChange?.(parseInt(e.target.value));
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
                align='left'
            />
        </Card>
    );
}
